import {
  airHelp,
  allInclusiveProtection,
  ancillaryBundle,
  baggageService,
  bankruptcyInsuranceCoverGenius,
  cabinBaggage,
  cancelForAnyReason,
  cancellationGuarantee,
  cancellationInsideEu,
  cancellationInsuranceCoverGenius,
  cancellationOutsideEu,
  checkInBaggage,
  comprehensiveInsuranceCoverGenius,
  cancelTwentyFourHours,
  flexibleTicket,
  mobileTravelPlan,
  onlineCheckIn,
  lounge,
  seatMap,
  simpleVisa,
  sms,
  travelDocsPost,
  travelInsuranceCoverGenius,
  travelInsuranceInsideEu,
  travelInsuranceOutsideEu,
  tripCancellationProtection,
  mutuallyExclusive,
  fastTrack,
  qPass,
  platinum,
  airportTransfer,
} from '@eti/products';
import { travelerForm } from '@eti/travelers';
import { constants, string } from '@eti/utils';
import {
  composeValidators,
  hasLengthGreaterThan,
  isBlockedEmail,
  isFutureDate,
  isOneOf,
  isRequired,
  isUniqueTraveler,
  isValidAdultBirthDate,
  isValidChildBirthDate,
  isValidDate,
  isValidEmail,
  isValidInfantBirthDate,
  isValidInfantWithNoFutureBirthDate,
  isValidPhoneNumber,
  isValidRyanAirEmail,
  isValidTaxId,
  matchesField,
  matchesFieldIfSelected,
  notMatchesField,
  onlyContainsValidCharacters,
} from '@eti/validation';
import { CONTACT_INFO_FORM_SECTION, agencyFields, contactFields } from '../constants/formFields';

const { getTravelerFormSectionName, LAST_NAME, FIRST_NAME, DATE_OF_BIRTH } = travelerForm;
const { ADULT, CHILD, INFANT } = constants.ageTypes;

export const getFullNameWarningRules = (travelers, texts) =>
  Array.isArray(travelers) && travelers.length > 0
    ? travelers.reduce((rules, traveler) => {
        const { id } = traveler;
        const travelerFormSectionName = getTravelerFormSectionName(id);
        const firstNameRule = isUniqueTraveler(id)({
          message: texts.anotherPassengerWithSameName,
        });
        const lastNameRules = composeValidators(
          notMatchesField(`${travelerFormSectionName}.${FIRST_NAME}-${id}`)({
            message: texts.sameFirstAndLastName,
          }),
          isUniqueTraveler(
            id,
            true,
          )({
            message: texts.anotherPassengerWithSameName,
          }),
        )(`${LAST_NAME}-${id}`);

        return {
          ...rules,
          [`${FIRST_NAME}-${id}`]: firstNameRule,
          [`${LAST_NAME}-${id}`]: lastNameRules,
        };
      }, {})
    : {};

const getRuleForAgeType = (arrivalDate, dateFormat, texts) => (ageType) => {
  switch (ageType) {
    case CHILD: {
      return isValidChildBirthDate(
        arrivalDate,
        dateFormat,
      )({
        message: texts.invalidChildBirthDate,
      });
    }
    case INFANT: {
      return isValidInfantBirthDate(
        arrivalDate,
        dateFormat,
      )({
        message: texts.invalidInfantBirthDate,
      });
    }
    case ADULT:
    default: {
      return isValidAdultBirthDate(
        arrivalDate,
        dateFormat,
      )({
        message: texts.invalidAdultBirthDate,
      });
    }
  }
};

export const getManulifeTravelersRules = (arrivalDate, dateFormat, travelers, texts) => {
  if (Array.isArray(travelers) && travelers.length > 0) {
    const ageRuleFor = getRuleForAgeType(arrivalDate, dateFormat, texts);

    return travelers.reduce((acc, { ageType, id, showDateOfBirth }) => {
      const travelerFormSectionName = getTravelerFormSectionName(id);
      const matchRuleTravelerForm = matchesField(
        `${travelerFormSectionName}.${DATE_OF_BIRTH}-${id}`,
      )({
        message: texts.manulifeDateOfBirthDoNotMatch,
      });
      const matchRuleAllInclusiveProtection = matchesFieldIfSelected(
        `${allInclusiveProtection.formSection}.travelers.traveler-${id}.dateOfBirth`,
        `${allInclusiveProtection.formSection}.value.value`,
      )({
        message: texts.manulifeDateOfBirthDoNotMatch,
      });
      const matchRuleTripCancellationProtection = matchesFieldIfSelected(
        `${tripCancellationProtection.formSection}.travelers.traveler-${id}.dateOfBirth`,
        `${tripCancellationProtection.formSection}.value.value`,
      )({
        message: texts.manulifeDateOfBirthDoNotMatch,
      });

      const travelerRules = {
        [`${tripCancellationProtection.MANULIFE_DATE_OF_BIRTH}-${id}-${ageType}`]:
          composeValidators(
            isValidDate(dateFormat)({ message: texts.dateOfBirth }),
            ageRuleFor(ageType),
            showDateOfBirth ? matchRuleTravelerForm : matchRuleAllInclusiveProtection,
          )(`${tripCancellationProtection.MANULIFE_DATE_OF_BIRTH}-${id}-${ageType}`),
        [`${allInclusiveProtection.MANULIFE_DATE_OF_BIRTH}-${id}-${ageType}`]: composeValidators(
          isValidDate(dateFormat)({ message: texts.dateOfBirth }),
          ageRuleFor(ageType),
          showDateOfBirth ? matchRuleTravelerForm : matchRuleTripCancellationProtection,
        )(`${allInclusiveProtection.MANULIFE_DATE_OF_BIRTH}-${id}-${ageType}`),
      };

      return { ...acc, ...travelerRules };
    }, {});
  }
  return {};
};

export const getProductRules = ({
  selectOneMessage,
  selectOneOptionMessage,
  validationEnabled,
}) => {
  return validationEnabled
    ? {
        [airHelp.name]: isRequired({ message: selectOneMessage }),
        [allInclusiveProtection.name]: isRequired({ message: selectOneOptionMessage }),
        [ancillaryBundle.name]: isRequired({ message: selectOneOptionMessage }),
        [baggageService.name]: isRequired({ message: selectOneMessage }),
        [bankruptcyInsuranceCoverGenius.name]: isRequired({ message: selectOneMessage }),
        [cabinBaggage.name]: composeValidators(
          isOneOf(
            ['true', 'false'],
            (value, validValue) => value.value === validValue || value === validValue,
          )({ message: selectOneMessage }),
          isRequired({ message: selectOneMessage }),
        )(),
        [cancelForAnyReason.name]: isRequired({ message: selectOneMessage }),
        [cancellationGuarantee.name]: isRequired({ message: selectOneMessage }),
        [cancellationInsideEu.name]: isRequired({ message: selectOneMessage }),
        [cancellationInsuranceCoverGenius.name]: isRequired({ message: selectOneMessage }),
        [cancellationOutsideEu.name]: isRequired({ message: selectOneMessage }),
        [checkInBaggage.name]: composeValidators(
          isOneOf(['true', 'false', 'combined'], (value, validValue) =>
            value?.value?.includes(validValue),
          )({ message: selectOneOptionMessage }),
          isRequired({ message: selectOneOptionMessage }),
        )(),
        [comprehensiveInsuranceCoverGenius.name]: isRequired({ message: selectOneMessage }),
        [cancelTwentyFourHours.name]: isRequired({ message: selectOneMessage }),
        [fastTrack.name]: isRequired({ message: selectOneMessage }),
        [flexibleTicket.name]: isRequired({ message: selectOneMessage }),
        [mobileTravelPlan.name]: isRequired({ message: selectOneMessage }),
        [mutuallyExclusive.name]: isRequired({ message: selectOneMessage }),
        [onlineCheckIn.name]: isRequired({ message: selectOneMessage }),
        [lounge.name]: isRequired({ message: selectOneMessage }),
        [qPass.name]: isRequired({ message: selectOneMessage }),
        [seatMap.name]: isRequired({ message: selectOneOptionMessage }),
        [simpleVisa.name]: isOneOf(
          [true, false, 'true', 'false'],
          (value, validValue) => value.value === validValue,
        )({ message: selectOneOptionMessage }),
        [sms.name]: isRequired({ message: selectOneMessage }),
        [travelDocsPost.name]: isRequired({ message: selectOneMessage }),
        [travelInsuranceCoverGenius.name]: isRequired({ message: selectOneMessage }),
        [travelInsuranceInsideEu.name]: isRequired({ message: selectOneMessage }),
        [travelInsuranceOutsideEu.name]: isRequired({ message: selectOneMessage }),
        [tripCancellationProtection.name]: isRequired({ message: selectOneOptionMessage }),
        [platinum.name]: isRequired({ message: selectOneMessage }),
        [airportTransfer.name]: isRequired({ message: selectOneMessage }),
      }
    : {
        [allInclusiveProtection.name]: isRequired({ message: selectOneOptionMessage }),
        [tripCancellationProtection.name]: isRequired({ message: selectOneOptionMessage }),
      };
};

const validationRules = (texts, configuration = {}) => {
  const {
    adultMinimumAge = 18,
    allowedCharacters,
    arrivalDate,
    blockedEmailDomains,
    dateFormat,
    enableProductValidation = true,
    enableRyanAirEmailValidation,
    marketingCarriers,
    taxIdData,
    travelers,
  } = configuration;
  const { taxIdFieldLabel, taxIdFieldFormat } = taxIdData;
  const {
    taxIdRequired,
    invalidTaxId,
    required: selectOneMessage,
    selectOneOptionRequired: selectOneOptionMessage,
  } = texts;
  const taxIdRequiredMessage =
    taxIdFieldLabel && taxIdRequired
      ? string.insertArgument(taxIdRequired, taxIdFieldLabel)
      : 'tax ID field is Required';
  const invalidTaxIdMessage =
    taxIdFieldLabel && invalidTaxId
      ? string.insertArgument(invalidTaxId, taxIdFieldLabel)
      : 'invalid format for tax ID';

  return {
    ...getProductRules({
      validationEnabled: enableProductValidation,
      selectOneMessage,
      selectOneOptionMessage,
    }),
    ...getManulifeTravelersRules(arrivalDate, dateFormat, travelers, texts),
    ...getFullNameWarningRules(travelers, texts),
    [agencyFields.AGENT_FIRST_NAME]: isRequired({
      message: texts.firstName,
    }),
    [agencyFields.AGENT_LAST_NAME]: isRequired({
      message: texts.lastName,
    }),
    [agencyFields.AGENT_EMAIL]: composeValidators(
      isRequired({ message: texts.mail }),
      isValidEmail({ message: texts.mail }),
      enableRyanAirEmailValidation
        ? isValidRyanAirEmail(marketingCarriers)({ message: texts.ryanAirMail })
        : () => null,
      isBlockedEmail(blockedEmailDomains)({ message: texts.emailBlock }),
    )(agencyFields.AGENT_EMAIL),
    [agencyFields.AGENT_PHONE]: composeValidators(
      isRequired({ message: texts.phone }),
      isValidPhoneNumber({ message: texts.phone }),
    )(agencyFields.AGENT_PHONE),
    [contactFields.ARRANGER_FIRST_NAME]: composeValidators(
      isRequired({ message: texts.firstName }),
      hasLengthGreaterThan(1)({ message: texts.firstName }),
      onlyContainsValidCharacters(allowedCharacters)({ message: texts.invalidCharacters }),
    )(contactFields.ARRANGER_FIRST_NAME),
    [contactFields.ARRANGER_LAST_NAME]: composeValidators(
      isRequired({ message: texts.lastName }),
      hasLengthGreaterThan(1)({ message: texts.lastName }),
      onlyContainsValidCharacters(allowedCharacters)({ message: texts.invalidCharacters }),
    )(contactFields.ARRANGER_LAST_NAME),
    [contactFields.EMAIL]: composeValidators(
      isRequired({ message: texts.mail }),
      isValidEmail({ message: texts.mail }),
      enableRyanAirEmailValidation
        ? isValidRyanAirEmail(marketingCarriers)({ message: texts.ryanAirMail })
        : () => null,
      isBlockedEmail(blockedEmailDomains)({ message: texts.emailBlock }),
    )(contactFields.EMAIL),
    [contactFields.EMAIL_CONFIRM]: composeValidators(
      isRequired({ message: texts.mail }),
      matchesField(`${CONTACT_INFO_FORM_SECTION}.${contactFields.EMAIL}`)({
        message: texts.emailConfirmDontMatch,
      }),
    )(contactFields.EMAIL_CONFIRM),
    [contactFields.EMERGENCY_LAST_NAME]: composeValidators(
      isRequired({ message: texts.lastName }),
      hasLengthGreaterThan(1)({ message: texts.lastName }),
      onlyContainsValidCharacters(allowedCharacters)({ message: texts.invalidCharacters }),
    )(contactFields.EMERGENCY_LAST_NAME),
    [contactFields.PHONE]: composeValidators(
      isRequired({ message: texts.phone }),
      isValidPhoneNumber({ message: texts.phone }),
    )(contactFields.PHONE),
    [contactFields.EMERGENCY_PHONE]: composeValidators(
      isRequired({ message: texts.phone }),
      isValidPhoneNumber({ message: texts.phone }),
    )(contactFields.EMERGENCY_PHONE),
    [travelerForm.FIRST_NAME]: composeValidators(
      isRequired({ message: texts.firstName }),
      hasLengthGreaterThan(1)({ message: texts.firstName }),
      onlyContainsValidCharacters(allowedCharacters)({ message: texts.invalidCharacters }),
    )(travelerForm.FIRST_NAME),
    [travelerForm.LAST_NAME]: composeValidators(
      isRequired({ message: texts.lastName }),
      hasLengthGreaterThan(1)({ message: texts.lastName }),
      onlyContainsValidCharacters(allowedCharacters)({ message: texts.invalidCharacters }),
    )(travelerForm.LAST_NAME),
    [`${travelerForm.DATE_OF_BIRTH}-${ADULT}`]: composeValidators(
      isRequired({ message: texts.dateOfBirth }),
      isValidDate(dateFormat)({ message: selectOneMessage }),
      isValidAdultBirthDate(
        arrivalDate,
        dateFormat,
        adultMinimumAge,
      )({ message: texts.invalidAdultBirthDate }),
    )(`${travelerForm.DATE_OF_BIRTH}-${ADULT}`),
    [`${travelerForm.DATE_OF_BIRTH}-${CHILD}`]: composeValidators(
      isRequired({ message: texts.dateOfBirth }),
      isValidDate(dateFormat)({ message: selectOneMessage }),
      isValidChildBirthDate(arrivalDate, dateFormat)({ message: texts.invalidChildBirthDate }),
    )(`${travelerForm.DATE_OF_BIRTH}-${CHILD}`),
    [`${travelerForm.DATE_OF_BIRTH}-${INFANT}`]: composeValidators(
      isRequired({ message: texts.dateOfBirth }),
      isValidDate(dateFormat)({ message: selectOneMessage }),
      isValidInfantBirthDate(arrivalDate, dateFormat)({ message: texts.invalidInfantBirthDate }),
      isValidInfantWithNoFutureBirthDate(dateFormat)({
        message: texts.invalidInfantBirthDateCheckForFutureDate,
      }),
    )(`${travelerForm.DATE_OF_BIRTH}-${INFANT}`),
    [travelerForm.GENDER]: isRequired({
      message: texts.gender,
    }),
    [travelerForm.TRAVEL_DOCUMENT_ISSUING_COUNTRY]: isRequired({
      message: texts.travelDocumentIssueCountry,
    }),
    [travelerForm.TRAVEL_DOCUMENT_NUMBER]: isRequired({
      message: texts.travelDocumentNumber,
    }),
    [travelerForm.TRAVEL_DOCUMENT_TYPE]: isRequired({
      message: texts.travelDocumentType,
    }),
    [travelerForm.TRAVEL_DOCUMENT_EXPIRY_DATE]: composeValidators(
      isRequired({ message: texts.travelDocumentExpiryDate }),
      isValidDate(dateFormat)({
        message: texts.travelDocumentExpiryDate,
      }),
      isFutureDate(dateFormat)({ message: texts.travelDocumentExpiryDate }),
    )(travelerForm.TRAVEL_DOCUMENT_EXPIRY_DATE),
    [travelerForm.TAX_ID_INPUT]: composeValidators(
      isRequired({ message: taxIdRequiredMessage }),
      isValidTaxId(taxIdFieldFormat)({ message: invalidTaxIdMessage }),
    )(travelerForm.TAX_ID_INPUT),
  };
};

export default validationRules;
